/* Daydream Made — Blind Book Revealer (vanilla JS, no build step) */
(function () {
  'use strict';

  const root = document.getElementById('revealer-root');
  if (!root) return;

  const RAILWAY_URL = (root.dataset.railwayUrl || '').replace(/\/+$/, '');
  const params = new URLSearchParams(window.location.search);
  const bookIdParam = (params.get('id') || '').trim();

  // Bookshop.org recommendations are prefetched when the book loads (during the sealed
  // state) so they're ready — or nearly — by the time the customer taps reveal. Resolves
  // to an array (possibly empty); never rejects.
  let recommendationsPromise = null;

  // --- Mood colors (mirrors daydream-matchmaker/public/mood-colors.js) ---
  const MOOD_COLORS = {
    'Atmospheric': '#7b8fa1', 'Cozy': '#dee4ff', 'Dark': '#5a4a6b', 'Eerie': '#4e6b5a',
    'Emotional': '#c47a7a', 'Empowering': '#5a77bf', 'Escapist': '#5b8fb9', 'Funny': '#c1cc9c',
    'Heartwarming': '#ddcaf9', 'Hopeful': '#8bb87a', 'Intense': '#b94e4e', 'Melancholic': '#7a7ab9',
    'Mind-Bending': '#8b5fbf', 'Nostalgic': '#a6beb4', 'Quirky': '#c5adec', 'Romantic': '#c47a8b',
    'Suspenseful': '#6b7a8b', 'Swoony': '#d47a9a', 'Tender': '#9ab9a8', 'Tense': '#8b6b5a',
    'Thought-Provoking': '#6b8bb9', 'Unsettling': '#5a6b5a', 'Whimsical': '#b98bcc', 'Witty': '#c1cc9c',
  };
  const ACCENT = '#5a77bf';
  const prefersReducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  // Genres hidden from the filter pills (still included in "All" and still
  // revealable by direct URL — just not surfaced as their own filter because
  // the inventory volume is too low for a dedicated browse view).
  const HIDDEN_GENRES = new Set([
    'Spicy',
    'Christian',
    'Christmas',
    'Graphic Novel',
    'Poetry',
    'Nonfiction',
    'Biography/Memoir',
  ]);

  // --- DOM helper ---
  function el(tag, attrs, ...children) {
    const node = document.createElement(tag);
    if (attrs) {
      for (const k in attrs) {
        const v = attrs[k];
        if (v == null || v === false) continue;
        if (k === 'className' || k === 'class') node.className = v;
        else if (k === 'style' && typeof v === 'object') Object.assign(node.style, v);
        else if (k === 'html') node.innerHTML = v;
        else if (k === 'on' && typeof v === 'object') {
          for (const evt in v) node.addEventListener(evt, v[evt]);
        } else {
          node.setAttribute(k, v === true ? '' : v);
        }
      }
    }
    for (const c of children.flat()) {
      if (c == null || c === false) continue;
      node.appendChild(typeof c === 'string' || typeof c === 'number'
        ? document.createTextNode(String(c))
        : c);
    }
    return node;
  }

  // --- Safe storage (Safari private mode safety, mirrored from matchmaker app.js) ---
  function safeGetItem(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }
  function safeSetItem(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* ignore */ }
  }

  // --- Analytics (gtag if present, else no-op) ---
  function trackEvent(name, props) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, props || {});
      }
    } catch (e) { /* analytics never breaks the page */ }
  }

  // --- API client ---
  async function api(path, { timeoutMs = 12000 } = {}) {
    if (!RAILWAY_URL) throw new Error('No Railway URL configured');
    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(`${RAILWAY_URL}${path}`, { signal: ctrl.signal });
      if (!res.ok) {
        const err = new Error(`API ${res.status}`);
        err.status = res.status;
        throw err;
      }
      return await res.json();
    } catch (err) {
      if (err.name === 'AbortError') {
        const e = new Error('Request timed out');
        e.code = 'timeout';
        throw e;
      }
      if (!navigator.onLine) {
        const e = new Error('Offline');
        e.code = 'offline';
        throw e;
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // --- Toast ---
  let toastTimeoutId = null;
  function showToast(message, duration = 2400) {
    let toast = root.querySelector('.dd-toast');
    if (!toast) {
      toast = el('div', { className: 'dd-toast' });
      root.appendChild(toast);
    }
    toast.textContent = message;
    void toast.offsetHeight;
    toast.classList.add('dd-toast--visible');
    if (toastTimeoutId) clearTimeout(toastTimeoutId);
    toastTimeoutId = setTimeout(() => toast.classList.remove('dd-toast--visible'), duration);
  }

  // --- Common renderers ---

  function renderLoading(eyebrow) {
    root.innerHTML = '';
    root.appendChild(el('div', { className: 'dd-container' },
      el('div', { className: 'dd-loading' },
        eyebrow ? el('div', { className: 'dd-loading__eyebrow dd-mono' }, eyebrow) : null,
        el('div', { className: 'dd-spinner', 'aria-label': 'Loading' }),
      ),
    ));
  }

  function renderError(err) {
    const offline = err && err.code === 'offline';
    const timeout = err && err.code === 'timeout';
    const title = offline ? 'You appear to be offline.' :
                  timeout ? 'That took too long.' :
                  'Something went sideways.';
    const body = offline ? 'Check your connection and try again.' :
                 timeout ? 'The reveal service is slow right now. Try again in a moment.' :
                 'The reveal service had trouble responding. Try again shortly.';
    root.innerHTML = '';
    root.appendChild(el('div', { className: 'dd-container' },
      el('div', { className: 'dd-error' },
        el('div', { className: 'dd-error__title' }, title),
        el('div', { className: 'dd-error__body' }, body),
        el('button', {
          className: 'dd-btn dd-btn--primary',
          on: { click: () => window.location.reload() },
        }, 'Try again'),
      ),
    ));
    trackEvent('reveal_error', { code: (err && err.code) || 'unknown' });
  }

  function renderNotFound(sku) {
    root.innerHTML = '';
    root.appendChild(el('div', { className: 'dd-container' },
      el('div', { className: 'dd-notfound' },
        el('div', { className: 'dd-notfound__title' }, "We couldn't find that book."),
        el('div', { className: 'dd-notfound__body' },
          'Double-check the label on your book — Book IDs look like ',
          el('span', { className: 'dd-mono', style: { color: ACCENT } }, 'YA-260103475'),
          '. You can also browse the catalog below.'),
        el('a', { className: 'dd-btn dd-btn--ghost', href: '/pages/reveal' }, 'Browse the catalog'),
      ),
    ));
    trackEvent('reveal_not_found', { sku });
  }

  // --- Reveal mode ---

  function renderStars(rating) {
    if (rating == null) return null;
    const r = Math.max(0, Math.min(5, Math.round(Number(rating))));
    return el('span', { className: 'dd-rating__stars', 'aria-label': `${rating} out of 5` },
      '★'.repeat(r) + '☆'.repeat(5 - r));
  }

  function formatNumber(n) {
    if (n == null || isNaN(n)) return '';
    return Number(n).toLocaleString();
  }

  function buildSubjectTags(subjectsString) {
    if (!subjectsString) return null;
    const items = String(subjectsString).split(/[|,]/).map(s => s.trim()).filter(Boolean);
    if (items.length === 0) return null;
    return el('div', { className: 'dd-tags' },
      items.map(s => el('span', { className: 'dd-tag' }, s)));
  }

  async function shareReveal(book) {
    const url = `${window.location.origin}/pages/reveal?id=${encodeURIComponent(book.sku)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: book.title || 'Blind book reveal', url });
        trackEvent('reveal_share', { sku: book.sku, method: 'native' });
        return;
      } catch (e) { /* user cancelled — fall through */ }
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        showToast('Link copied');
        trackEvent('reveal_share', { sku: book.sku, method: 'clipboard' });
        return;
      } catch (e) { /* ignored */ }
    }
    window.prompt('Copy this link:', url);
  }

  function renderSealed(book) {
    root.innerHTML = '';

    const hero = el('div', { className: 'dd-hero' });
    if (book.coverUrl) {
      hero.appendChild(el('div', {
        className: 'dd-hero__bg',
        style: { backgroundImage: `url("${book.coverUrl}")` },
      }));
    } else {
      hero.appendChild(el('div', { className: 'dd-hero__bg dd-hero__bg--neutral' }));
    }
    if (book.labelUrl) {
      const labelImg = el('img', { src: book.labelUrl, alt: 'Sealed book label' });
      hero.appendChild(el('div', { className: 'dd-hero__label' }, labelImg));
    }
    hero.appendChild(el('div', { className: 'dd-hero__id dd-mono' }, book.sku));

    const sealedRoot = el('div', { className: 'dd-reveal' },
      hero,
      el('div', { className: 'dd-sealed-copy' }, 'Your blind date is waiting.'),
      el('button', {
        className: 'dd-btn dd-btn--primary',
        on: {
          click: () => {
            trackEvent('reveal_button_tap', { sku: book.sku });
            transitionToRevealed(sealedRoot, book);
          },
        },
      }, 'Reveal my book'),
    );

    root.appendChild(el('div', { className: 'dd-container' }, sealedRoot));
  }

  function transitionToRevealed(sealedNode, book) {
    if (prefersReducedMotion) { renderRevealed(book); return; }
    sealedNode.classList.add('dd-fading-out');
    setTimeout(() => renderRevealed(book), 400);
  }

  // --- Book Marks critic reviews (synchronous; we get the widget URL from the API) ---
  // We render Book Marks' own iframe directly rather than loading their bookmarks.js loader:
  // that script scans the DOM only at DOMContentLoaded and offers no re-init hook, so it
  // would never pick up markup injected after the reveal tap.
  function buildReviewsBlock(book) {
    if (!book.bookmarksReviewUrl) return null;
    const frame = el('iframe', {
      className: 'dd-reviews__frame',
      src: book.bookmarksReviewUrl,
      scrolling: 'no',
      loading: 'lazy',
      title: 'Critic reviews from Book Marks',
    });
    return el('div', { className: 'dd-reviews' },
      el('h2', { className: 'dd-section-title' }, 'Critic reviews'),
      el('div', { className: 'dd-reviews__frame-wrap' }, frame),
      el('div', { className: 'dd-reviews__attr' }, 'Aggregated by Book Marks · Literary Hub'),
    );
  }

  // --- Bookshop.org recommendations (async; populated when the prefetch resolves) ---
  function makeRecCard(rec, book) {
    const card = el('a', {
      className: 'dd-rec-card',
      href: rec.bookshopUrl,
      target: '_blank',
      rel: 'sponsored nofollow noopener',
      on: { click: () => trackEvent('reveal_rec_click', { sku: book.sku, isbn13: rec.isbn13 }) },
    });
    if (rec.coverUrl) {
      const img = el('img', {
        className: 'dd-rec-card__img',
        src: rec.coverUrl,
        alt: '',
        loading: 'lazy',
        decoding: 'async',
      });
      // A null Google thumbnail falls back to an OpenLibrary URL that 404s when missing —
      // swap a broken cover for a neutral placeholder rather than a torn-image icon.
      img.addEventListener('error', () => {
        img.replaceWith(el('div', { className: 'dd-rec-card__img dd-rec-card__img--ph' }));
      }, { once: true });
      card.appendChild(img);
    } else {
      card.appendChild(el('div', { className: 'dd-rec-card__img dd-rec-card__img--ph' }));
    }
    card.appendChild(el('div', { className: 'dd-rec-card__title' }, rec.title || ''));
    const author = (rec.authors || []).filter(Boolean).join(', ');
    if (author) card.appendChild(el('div', { className: 'dd-rec-card__author' }, author));
    card.appendChild(el('div', { className: 'dd-rec-card__cta' }, 'View on Bookshop.org ↗'));
    return card;
  }

  function populateRecommendations(container, recs, book) {
    if (!container || !Array.isArray(recs) || recs.length === 0) return; // stays hidden
    // Derive the affiliate storefront from a recommendation link so the mission line's
    // Bookshop link is attributed too — without hardcoding the affiliate ID in the theme.
    const m = (recs[0].bookshopUrl || '').match(/\/a\/([^/]+)\//);
    const storefront = m ? `https://bookshop.org/a/${m[1]}` : 'https://bookshop.org';

    const grid = el('div', { className: 'dd-recs' });
    recs.forEach(rec => grid.appendChild(makeRecCard(rec, book)));

    container.innerHTML = '';
    container.appendChild(el('hr', { className: 'dd-rule' }));
    container.appendChild(el('h2', { className: 'dd-section-title' }, 'You might also enjoy'));
    container.appendChild(el('p', { className: 'dd-recs__intro' },
      "Half the fun is the mystery — but we get it: sometimes it's fun to just buy exactly ",
      "what you're in the mood for. Not every book has to be a blind date."));
    container.appendChild(el('p', { className: 'dd-recs__mission' },
      'Each pick lives on ',
      el('a', { href: storefront, target: '_blank', rel: 'sponsored nofollow noopener' }, 'Bookshop.org'),
      ', an online bookstore on a quiet mission: a share of every order goes to independent, ',
      'local bookshops. So your next read helps keep real bookstores — the kind with creaky ',
      'floors and hand-written staff picks — on the map.'));
    container.appendChild(grid);
    container.appendChild(el('p', { className: 'dd-recs__disclosure' },
      'As a Bookshop.org affiliate, Daydream Made may earn a small commission on purchases ',
      'made through these links, at no extra cost to you. It helps us keep the blind dates coming.'));
    container.hidden = false;
    trackEvent('reveal_recs_shown', { sku: book.sku, count: recs.length });
  }

  function renderRevealed(book) {
    root.innerHTML = '';

    // Reveal image: prefer publisher cover, fall back to label. Listen for image load failure
    // so a broken coverUrl quietly degrades to the Placid label.
    const imgSrc = book.coverUrl || book.labelUrl;
    const coverImg = el('img', { src: imgSrc, alt: book.title || 'Book cover' });
    if (book.coverUrl && book.labelUrl && book.coverUrl !== book.labelUrl) {
      coverImg.addEventListener('error', () => {
        if (coverImg.src !== book.labelUrl) {
          coverImg.src = book.labelUrl;
          trackEvent('reveal_image_fallback', { sku: book.sku });
        }
      }, { once: true });
    }

    const titleNode = book.title
      ? el('h1', { className: 'dd-title', tabindex: '-1' }, book.title)
      : null;

    const authorText = (book.authors || []).filter(Boolean).join(', ');
    const authorNode = authorText
      ? el('div', { className: 'dd-author' },
          'by ', authorText,
          book.publishYear ? el('span', { style: { color: 'var(--ddm-fg-dim)' } }, ` · ${book.publishYear}`) : null)
      : null;

    let ratingNode = null;
    if (book.goodreadsRating != null) {
      ratingNode = el('div', { className: 'dd-rating' },
        renderStars(book.goodreadsRating),
        el('span', null, Number(book.goodreadsRating).toFixed(2)),
        book.goodreadsCount != null ? el('span', null, `(${formatNumber(book.goodreadsCount)} ratings)`) : null,
      );
    }

    const badgeChildren = [];
    if (book.genre) badgeChildren.push(el('span', { className: 'dd-badge dd-badge--genre' }, book.genre));
    if (book.pace) badgeChildren.push(el('span', { className: 'dd-badge' }, book.pace));
    if (book.pageCount) badgeChildren.push(el('span', { className: 'dd-badge' }, `${book.pageCount} pages`));
    (book.moods || []).forEach(m => {
      const color = MOOD_COLORS[m];
      badgeChildren.push(el('span', {
        className: 'dd-badge dd-badge--mood',
        style: color ? { '--mood-color': color } : null,
      }, m));
    });
    const badgeRow = badgeChildren.length > 0
      ? el('div', { className: 'dd-badges' }, badgeChildren)
      : null;

    const subjectsBlock = (() => {
      const tags = buildSubjectTags(book.subjects);
      if (!tags) return null;
      return el('div', null,
        el('h2', { className: 'dd-section-title' }, 'Subjects & Themes'),
        tags,
      );
    })();

    const synopsisBlock = book.synopsis
      ? el('div', null,
          el('h2', { className: 'dd-section-title' }, 'About your book'),
          el('p', { className: 'dd-synopsis' }, book.synopsis),
        )
      : null;

    const contentNotesBlock = (book.triggerWarnings && book.triggerWarnings.length)
      ? el('div', null,
          el('h2', { className: 'dd-section-title' }, 'Content notes'),
          el('div', { className: 'dd-tags' },
            book.triggerWarnings.map(t => el('span', { className: 'dd-tag dd-tag--note' }, t))),
        )
      : null;

    const traitsBlock = (book.otherTraits && book.otherTraits.length)
      ? el('div', null,
          el('h2', { className: 'dd-section-title' }, 'Good to know'),
          el('div', { className: 'dd-tags' },
            book.otherTraits.map(t => el('span', { className: 'dd-tag' }, t))),
        )
      : null;

    const ctaBlock = el('div', { className: 'dd-cta' },
      el('a', {
        href: '/collections/blind-date-with-a-book',
        on: { click: () => trackEvent('reveal_cta_tap', { sku: book.sku, target: 'shop' }) },
      }, 'Find your next blind date →'),
      el('div', { className: 'dd-cta__secondary' },
        el('a', {
          href: '/pages/reveal',
          on: { click: () => trackEvent('reveal_cta_tap', { sku: book.sku, target: 'catalog' }) },
        }, 'Reveal another book'),
      ),
    );

    const shareRow = el('div', { className: 'dd-share-row' },
      el('button', {
        className: 'dd-share-btn',
        on: { click: () => shareReveal(book) },
      }, 'Share this book'),
    );

    const reviewsBlock = buildReviewsBlock(book);
    // Recommendations arrive async — start hidden, populate (with its own leading rule)
    // when the prefetch resolves, and stay hidden if there are none.
    const recsSection = el('div', { className: 'dd-recs-section', hidden: true });

    const revealedSections = [
      el('div', { className: 'dd-cover' }, coverImg),
      titleNode,
      authorNode,
      ratingNode,
      badgeRow,
      shareRow,
      subjectsBlock ? el('hr', { className: 'dd-rule' }) : null,
      subjectsBlock,
      synopsisBlock ? el('hr', { className: 'dd-rule' }) : null,
      synopsisBlock,
      contentNotesBlock ? el('hr', { className: 'dd-rule' }) : null,
      contentNotesBlock,
      traitsBlock ? el('hr', { className: 'dd-rule' }) : null,
      traitsBlock,
      reviewsBlock ? el('hr', { className: 'dd-rule' }) : null,
      reviewsBlock,
      recsSection,
      el('hr', { className: 'dd-rule' }),
      ctaBlock,
    ].filter(Boolean);

    const wrapper = el('div', { className: 'dd-revealed', tabindex: '-1' });
    revealedSections.forEach((node, i) => {
      node.style.animationDelay = `${i * 100}ms`;
      node.classList.add('dd-fade-in');
      wrapper.appendChild(node);
    });

    root.appendChild(el('div', { className: 'dd-container' }, wrapper));

    (titleNode || wrapper).focus();

    trackEvent('reveal_revealed', { sku: book.sku, hasCover: !!book.coverUrl });
    if (reviewsBlock) trackEvent('reveal_reviews_shown', { sku: book.sku });
    if (recommendationsPromise) {
      recommendationsPromise.then(recs => populateRecommendations(recsSection, recs, book));
    }
  }

  async function startRevealMode(rawSku) {
    const sku = String(rawSku).trim().toUpperCase();
    trackEvent('reveal_page_view', { sku });
    renderLoading(sku);
    let response;
    try {
      response = await api(`/api/reveal/book/${encodeURIComponent(sku)}`);
    } catch (err) {
      return renderError(err);
    }
    if (!response || !response.found || !response.book) {
      return renderNotFound(sku);
    }
    // Prefetch Bookshop recommendations in parallel with the sealed→reveal interaction.
    recommendationsPromise = api(`/api/reveal/recommendations/${encodeURIComponent(sku)}`)
      .then(r => (r && Array.isArray(r.recommendations)) ? r.recommendations : [])
      .catch(() => []);
    renderSealed(response.book);
  }

  // --- Catalog mode ---

  const catalogState = {
    currentPage: 1,
    perPage: 24,
    genre: null, // null = All
    hasMore: true,
    loading: false,
    grid: null,
    loadMoreBtn: null,
    pillsRow: null,
  };

  function makeTile(book) {
    const tile = el('a', {
      href: `?id=${encodeURIComponent(book.sku)}`,
      className: 'dd-tile',
      'aria-label': `Reveal book ${book.sku}`,
      on: {
        click: () => trackEvent('catalog_tile_tap', { sku: book.sku, genre: book.genre || null }),
      },
    });
    if (book.labelUrl) {
      const img = el('img', {
        className: 'dd-tile__img',
        src: book.labelUrl,
        alt: '',
        loading: 'lazy',
        decoding: 'async',
      });
      tile.appendChild(img);
    }
    tile.appendChild(el('div', { className: 'dd-tile__id' }, book.sku));
    tile.appendChild(el('div', { className: 'dd-tile__overlay' },
      book.genre ? el('span', { className: 'dd-tile__genre' }, book.genre) : null,
    ));
    return tile;
  }

  async function fetchCatalogPage({ reset = false } = {}) {
    if (catalogState.loading) return;
    catalogState.loading = true;

    if (reset) {
      catalogState.currentPage = 1;
      catalogState.hasMore = true;
      if (catalogState.grid) catalogState.grid.innerHTML = '';
    }

    catalogState.loadMoreBtn.textContent = 'Loading…';
    catalogState.loadMoreBtn.disabled = true;

    const qp = new URLSearchParams();
    qp.set('page', String(catalogState.currentPage));
    qp.set('per_page', String(catalogState.perPage));
    if (catalogState.genre) qp.set('genre', catalogState.genre);

    let response;
    try {
      response = await api(`/api/reveal/catalog?${qp.toString()}`);
    } catch (err) {
      catalogState.loading = false;
      catalogState.loadMoreBtn.textContent = 'Try again';
      catalogState.loadMoreBtn.disabled = false;
      showToast('Could not load the catalog. Try again.');
      trackEvent('catalog_error', { code: (err && err.code) || 'unknown' });
      return;
    }

    const books = Array.isArray(response && response.books) ? response.books : [];
    if (books.length === 0 && catalogState.currentPage === 1) {
      catalogState.grid.appendChild(el('div', { className: 'dd-empty' },
        'No books match this filter yet. Try another genre.'));
    } else {
      for (const book of books) {
        catalogState.grid.appendChild(makeTile(book));
      }
    }

    catalogState.hasMore = !!(response && response.has_more);
    catalogState.currentPage = ((response && response.page) || catalogState.currentPage) + 1;
    catalogState.loading = false;

    catalogState.loadMoreBtn.textContent = 'Load more';
    catalogState.loadMoreBtn.disabled = false;
    catalogState.loadMoreBtn.parentElement.style.display = catalogState.hasMore ? '' : 'none';
  }

  function changeGenre(genre, pillEl) {
    catalogState.pillsRow.querySelectorAll('.dd-pill').forEach(p => p.classList.remove('dd-pill--active'));
    if (pillEl) pillEl.classList.add('dd-pill--active');
    catalogState.genre = genre;
    trackEvent('catalog_filter_change', { genre: genre || 'All' });
    fetchCatalogPage({ reset: true });
  }

  function renderCatalogShell(initialCount) {
    root.innerHTML = '';

    const counterText = initialCount
      ? `${formatNumber(initialCount.totalBooks)} blind dates · ${formatNumber(initialCount.uniqueTitles)} unique titles and counting`
      : 'Counting blind dates…';

    const idForm = el('form', {
      className: 'dd-id-form',
      on: {
        submit: (e) => {
          e.preventDefault();
          const inputEl = idForm.querySelector('.dd-id-form__input');
          const value = (inputEl.value || '').trim().toUpperCase();
          if (!value) return;
          trackEvent('catalog_id_form_submit', { value });
          window.location.href = `?id=${encodeURIComponent(value)}`;
        },
      },
    },
      el('h2', { className: 'dd-id-form__title' }, 'Have a book? Enter your Book ID to reveal it.'),
      el('input', {
        className: 'dd-id-form__input',
        type: 'text',
        autocomplete: 'off',
        autocapitalize: 'characters',
        spellcheck: 'false',
        placeholder: 'e.g. YA-260103475',
        'aria-label': 'Book ID',
      }),
      el('div', { className: 'dd-id-form__hint' }, 'YA- · R- · M- · TH- · HF- · Fan-'),
      el('button', { className: 'dd-btn dd-btn--primary', type: 'submit' }, 'Reveal'),
    );

    const apiGenres = (initialCount && Array.isArray(initialCount.genres))
      ? initialCount.genres.filter(g => !HIDDEN_GENRES.has(g))
      : [];
    const pillLabels = ['All', ...apiGenres];

    const pillsRow = el('div', { className: 'dd-pills' });
    pillLabels.forEach((label, i) => {
      const isAll = label === 'All';
      const pill = el('button', {
        className: 'dd-pill' + (i === 0 ? ' dd-pill--active' : ''),
        type: 'button',
        on: { click: () => changeGenre(isAll ? null : label, pill) },
      }, label);
      pillsRow.appendChild(pill);
    });

    const grid = el('div', { className: 'dd-grid' });
    const loadMoreBtn = el('button', {
      className: 'dd-btn dd-btn--ghost',
      type: 'button',
      on: { click: () => fetchCatalogPage() },
    }, 'Load more');
    const loadMoreWrapper = el('div', { className: 'dd-load-more' }, loadMoreBtn);

    catalogState.grid = grid;
    catalogState.loadMoreBtn = loadMoreBtn;
    catalogState.pillsRow = pillsRow;

    root.appendChild(el('div', { className: 'dd-container dd-container--wide' },
      el('div', { className: 'dd-catalog__header' },
        el('h1', { className: 'dd-catalog__title' }, 'Reveal your blind date'),
        el('div', { className: 'dd-catalog__counter' }, counterText),
      ),
      idForm,
      pillsRow,
      grid,
      loadMoreWrapper,
    ));
  }

  async function startCatalogMode() {
    trackEvent('catalog_page_view');

    // Fetch count and first catalog page in parallel — count is cheap (cached), so the
    // first paint can show real totals instead of "Counting…".
    let countData = null;
    try {
      countData = await api('/api/reveal/count');
    } catch (e) { /* non-fatal — render with placeholder counter */ }

    renderCatalogShell(countData);
    fetchCatalogPage({ reset: true });
  }

  // --- Boot ---

  if (!RAILWAY_URL) {
    root.appendChild(el('div', { className: 'dd-container' },
      el('div', { className: 'dd-error' },
        el('div', { className: 'dd-error__title' }, 'Reveal service unavailable'),
        el('div', { className: 'dd-error__body' },
          'The reveal service is not configured. (Set the ',
          el('span', { className: 'dd-mono' }, 'shop.metafields.custom.railway_url'),
          ' metafield in Shopify Admin.)'),
      ),
    ));
    return;
  }

  if (bookIdParam) {
    startRevealMode(bookIdParam);
  } else {
    startCatalogMode();
  }
})();
